import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../utils/constants';

/**
 * DossierPanel — appears when the selected client already has a preloaded
 * CV + project ("dossier"), auto-captured by previously generated deliverables
 * (patent, book, whitepaper, econometric study, case study, policy paper).
 *
 * By default (autoApply) it LOADS the saved CV / project / name into the form as
 * soon as the client is selected, so operators always start from the client's
 * canonical data and cannot silently generate a document from different inputs.
 * They can still replace it explicitly. When autoApply is false it behaves as a
 * suggestion (buttons the operator clicks).
 *
 * Props:
 *   clientId      — selected client id. Renders nothing if falsy or no dossier.
 *   onUseCv       — (text, {auto}) => void|false. Omit on screens with no CV input.
 *   onUseProject  — (text, {auto}) => void|false. Omit on screens with no project input.
 *                   `auto` is true only for the automatic load on client selection;
 *                   return false to say "I kept what the operator attached".
 *   onUseName     — (name, {auto}) => void. Sets the inventor/author/applicant name
 *                   from the CV's legal name (canonical), not the account name.
 *   autoApply     — auto-load CV/project/name on client selection (default false).
 *   compact       — smaller styling when embedded in dense forms.
 *   projectAttachReplaces — this screen ATTACHES a project file, and the backend adopts
 *                   it as the case's project (letters). Only changes the wording of the
 *                   pinned-project note so it matches what actually happens.
 */
export default function DossierPanel({
  clientId, onUseCv, onUseProject, onUseName,
  autoApply = false, compact = false, projectAttachReplaces = false,
}) {
  const [summary, setSummary] = useState(null);
  const [applied, setApplied] = useState({ cv: false, project: false });
  const [busy, setBusy] = useState(false);

  const fetchText = useCallback(async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/clients/${clientId}/dossier?include_text=true`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data || {};
  }, [clientId]);

  // Push dossier data into the form. `data` carries cv_text/project_text (from the
  // include_text fetch) and petitioner_name (from the summary).
  // `opts.auto` tells the form this came from the automatic load on client selection,
  // NOT from a click: the fetch takes a moment, and an operator who attaches a NEW
  // project meanwhile must not have it silently replaced by the saved one.
  const applyData = useCallback((data, which, opts = {}) => {
    const did = { cv: false, project: false };
    if ((which === 'cv' || which === 'both') && onUseCv && data.cv_text) {
      did.cv = onUseCv(data.cv_text, opts) !== false;
    }
    if ((which === 'project' || which === 'both') && onUseProject && data.project_text) {
      did.project = onUseProject(data.project_text, opts) !== false;
    }
    if ((which === 'name' || which === 'both') && onUseName && data.petitioner_name) {
      onUseName(data.petitioner_name, opts);
    }
    return did;
  }, [onUseCv, onUseProject, onUseName]);

  useEffect(() => {
    let cancelled = false;
    setSummary(null);
    setApplied({ cv: false, project: false });
    if (!clientId) return undefined;
    const token = localStorage.getItem('token');
    (async () => {
      try {
        const res = await axios.get(`${API}/clients/${clientId}/dossier`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Se conserva tambien cuando no hay dossier usable pero el backend
        // avisa que descarto campos ilegibles: si no, el panel desaparece
        // sin explicacion y el operador no sabe por que.
        const payload = res.data || {};
        const data = (payload.has_dossier || payload.needs_reupload) ? payload : null;
        if (cancelled) return;
        setSummary(data);
        // Auto-apply on selection so the operator ALWAYS starts from the client's
        // saved CV/project/name (they can still replace it explicitly below).
        if (data && autoApply) {
          const full = await fetchText();
          if (cancelled) return;
          const merged = { ...full, petitioner_name: data.petitioner_name };
          const did = applyData(merged, 'both', { auto: true });
          if (!cancelled) setApplied((p) => ({ ...p, ...did }));
        }
      } catch (e) {
        if (!cancelled) setSummary(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally keyed on clientId only: apply once per client selection.
  }, [clientId]);

  const apply = async (which) => {
    try {
      setBusy(true);
      const data = await fetchText();
      const merged = { ...data, petitioner_name: summary && summary.petitioner_name };
      const did = applyData(merged, which, { auto: false });
      setApplied((p) => ({ ...p, ...did }));
    } catch (e) {
      // Best-effort: if it fails, the operator just uploads/types as usual.
      console.error('DossierPanel apply failed', e);
    } finally {
      setBusy(false);
    }
  };

  if (!clientId) return null;
  const hasDossier = !!(summary && summary.has_dossier);

  const showCv = hasDossier && summary.has_cv && typeof onUseCv === 'function';
  const showProject = hasDossier && summary.has_project && typeof onUseProject === 'function';
  // El backend filtro campos que no son prosa legible (quedaron de una
  // extraccion vieja). Hay que decirlo: la primera version de este guard hacia
  // desaparecer el panel entero y parecia que la app se habia roto.
  const needsReupload = !!(summary && summary.needs_reupload);
  if (!showCv && !showProject && !needsReupload) return null;

  if (!showCv && !showProject) {
    return (
      <div
        style={{
          border: '1px solid #fcd34d',
          background: '#fffbeb',
          borderRadius: 8,
          padding: compact ? '10px 12px' : '12px 14px',
          marginBottom: 16,
        }}
      >
        <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 4, fontSize: 14 }}>
          ⚠️ Este cliente no tiene datos precargados utilizables
        </div>
        <div style={{ color: '#78350f', fontSize: 13, lineHeight: 1.5 }}>
          {summary.unusable_notice
            || 'Lo que había guardado de este cliente no es texto legible. Subí los archivos abajo.'}
        </div>
      </div>
    );
  }

  const srcLabel = hasDossier
    ? {
        business_plan: 'plan de negocios',
        patent: 'patente',
        book: 'libro',
        whitepaper: 'whitepaper',
        econometric_study: 'estudio econométrico',
        case_study: 'caso de estudio',
        policy_paper: 'policy paper',
        manual: 'carga manual',
      }[summary.source_doc_type] || summary.source_doc_type || ''
    : '';

  let fmtDate = '';
  if (hasDossier && summary.updated_at) {
    const d = new Date(summary.updated_at);
    if (!Number.isNaN(d.getTime())) fmtDate = d.toLocaleDateString();
  }

  const origin = srcLabel || fmtDate
    ? ` (último uso${srcLabel ? `: ${srcLabel}` : ''}${fmtDate ? `, ${fmtDate}` : ''})`
    : '';

  const petitionerName = (summary && summary.petitioner_name) || '';

  // Data available that the operator has NOT applied yet → remind them (only in the
  // non-auto mode; when autoApply already loaded it, we show the "applied" state).
  const autoApplied = autoApply && (applied.cv || applied.project);
  const notApplied = !autoApplied && ((showCv && !applied.cv) || (showProject && !applied.project));

  const btn = {
    padding: compact ? '5px 10px' : '6px 12px',
    borderRadius: 6,
    border: '1px solid #2563eb',
    background: '#fff',
    color: '#2563eb',
    cursor: busy ? 'wait' : 'pointer',
    fontSize: 13,
    fontWeight: 600,
    opacity: busy ? 0.6 : 1,
  };

  return (
    <div
      style={{
        border: '1px solid #bfdbfe',
        background: '#eff6ff',
        borderRadius: 8,
        padding: compact ? '10px 12px' : '12px 14px',
        marginBottom: 16,
      }}
    >
      <div style={{ fontWeight: 700, color: '#1e3a8a', marginBottom: 4, fontSize: 14 }}>
        📎 Datos precargados de este cliente
      </div>
      {needsReupload && (
        // Caso mixto: algo se pudo reusar y algo se descarto. El operador tiene
        // que saber cual de los dos le falta subir.
        <div style={{
          background: '#fffbeb',
          border: '1px solid #fcd34d',
          borderRadius: 6,
          padding: '7px 10px',
          margin: '6px 0 8px',
          color: '#78350f',
          fontSize: 12.5,
          lineHeight: 1.45,
        }}>
          ⚠️ {summary.unusable_notice}
        </div>
      )}
      <div style={{ fontSize: 12, color: '#3b5573', marginBottom: petitionerName ? 4 : 10 }}>
        {autoApply
          ? `Se usará el CV y/o proyecto ya guardados${origin} para mantener coherencia con los demás documentos del caso.`
          : `Reutiliza el CV y/o proyecto ya guardados${origin} en lugar de volver a adjuntarlos.`}
      </div>
      {petitionerName && (
        <div style={{ fontSize: 12, color: '#1e3a8a', marginBottom: 10 }}>
          👤 Titular del caso (según el CV): <strong>{petitionerName}</strong>
        </div>
      )}
      {hasDossier && summary.project_locked && (
        <div style={{ fontSize: 12, color: '#1e3a8a', marginBottom: 10 }}>
          📌 El proyecto del caso está <strong>fijado por la operadora</strong>
          {summary.project_filename ? ` (${summary.project_filename})` : ''}.{' '}
          {projectAttachReplaces
            ? 'Si adjuntás otro proyecto acá, ese pasa a ser el proyecto del caso (y se recalculan las cifras del paquete).'
            : 'Si lo reemplazás sólo en este formulario, el proyecto del caso no cambia: para cambiarlo, hacelo en la ficha del cliente.'}
        </div>
      )}
      {autoApplied && (
        <div
          style={{
            background: '#dcfce7',
            border: '1px solid #86efac',
            borderRadius: 6,
            padding: '8px 10px',
            marginBottom: 10,
            fontSize: 12,
            color: '#166534',
          }}
        >
          ✓ <strong>Datos del cliente cargados automáticamente.</strong> Se usarán estos en el documento.
          Si este documento necesita datos distintos, reemplazalos abajo.
        </div>
      )}
      {notApplied && (
        <div
          style={{
            background: '#fef3c7',
            border: '1px solid #fcd34d',
            borderRadius: 6,
            padding: '8px 10px',
            marginBottom: 10,
            fontSize: 12,
            color: '#92400e',
          }}
        >
          ⚠️ <strong>Recomendado:</strong> usá los datos precargados. Los documentos anteriores de
          este cliente se generaron con este CV/proyecto — adjuntar datos distintos puede crear{' '}
          <strong>incoherencias</strong> entre los documentos del caso.
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {showCv && (
          <button type="button" style={btn} disabled={busy} onClick={() => apply('cv')}>
            {applied.cv
              ? '✓ CV cargado'
              : `${autoApply ? 'Recargar' : 'Usar'} CV precargado${summary.cv_chars ? ` (${summary.cv_chars} car.)` : ''}`}
          </button>
        )}
        {showProject && (
          <button type="button" style={btn} disabled={busy} onClick={() => apply('project')}>
            {applied.project
              ? '✓ Proyecto cargado'
              : `${autoApply ? 'Recargar' : 'Usar'} proyecto precargado${summary.project_chars ? ` (${summary.project_chars} car.)` : ''}`}
          </button>
        )}
        {showCv && showProject && (
          <button
            type="button"
            style={{ ...btn, background: '#2563eb', color: '#fff' }}
            disabled={busy}
            onClick={() => apply('both')}
          >
            {applied.cv && applied.project ? '✓ Ambos cargados' : 'Usar ambos'}
          </button>
        )}
      </div>
      {busy && (
        <div style={{ fontSize: 11, color: '#3b5573', marginTop: 6 }}>Cargando datos precargados…</div>
      )}
    </div>
  );
}
